import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import { avatars } from "src/assets/images"
import LeagueFrame from "src/components/LeagueFrame"
import { playUiSound } from "src/utils/audio"
import { DEFAULT_AVATAR, seatPictureFor } from "src/utils/cosmetics"
import { gameModeName, isSurvival } from "src/utils/gameModes"
import { mapLabel } from "src/utils/mapSizes"
import { formatPoints } from "src/utils/ranking"

import "./MatchIntro.css"

/* The screen that opens a game (owner, 2026-10-08: "before going to the game map, we have to open
   for a moment ... the players that play, the classes and icons, the ranking and the names"). Once
   per game per browser, on the opening board (tick 0), for `INTRO_MS` (ten seconds since
   2026-10-09, owner: "a little bit longer"); a press or a key ends it
   early. It reads the seats the game payload already carries and says nothing the roster does not.

   `hvz-intro-<id>` remembers it was shown, so a reload at tick 0 goes straight to the map. Storage
   can throw (a private window); then it shows once per mount, which is the same promise. */
export const INTRO_MS = 10000
const MAX_SEATS_SHOWN = 6

const storageKey = (gameId) => `hvz-intro-${gameId}`

export const introSeen = (gameId) => {
  try {
    return window.localStorage.getItem(storageKey(gameId)) === "1"
  } catch {
    return false
  }
}

const rememberIntro = (gameId) => {
  try {
    window.localStorage.setItem(storageKey(gameId), "1")
  } catch {
    // Nothing to remember it with; the view's own state still ends it.
  }
}

// The two sides in a fixed order, humanity on the left, each listing its people first.
const sides = (players) =>
  ["human", "zombie"]
    .map((team) => ({
      team,
      seats: players
        .filter((player) => player.team === team)
        .sort((a, b) => Number(a.user?.isBot) - Number(b.user?.isBot)),
    }))
    .filter((side) => side.seats.length)

const Seat = ({ seat, t, ownAvatar }) => (
  <li className="intro-seat">
    {/* The person and nothing about the game (owner, 2026-10-09): their picture, in the frame of
        their league, with their name and standing beside it. The class and the map icon belong
        to the board, and the board has not opened yet. */}
    <LeagueFrame league={seat.user?.league} bot={Boolean(seat.user?.isBot ?? seat.user?.is_bot)} size="lg">
      <span className="intro-look">
        <img src={avatars[seatPictureFor(seat, ownAvatar)] || avatars[DEFAULT_AVATAR]} alt="" />
      </span>
    </LeagueFrame>
    <span className="intro-seat-text">
      <span className="intro-name">{seat.user?.username}</span>
      <span className="intro-standing">
        {seat.user?.isBot
          ? t(`game.forces.difficulty_${seat.botDifficulty || "easy"}`)
          : `${formatPoints(seat.user?.rankingPoints)} ${t("ranking.pts")}`}
      </span>
    </span>
  </li>
)

/* The games whose opening has sounded, this page load (requirement 36, Z2). Keyed on the game as
   `GameOverModal`'s verdict is, so a second mount — StrictMode's, or the view remounting under the
   route's fade — cannot play it twice; a reload at tick 0 skips the intro and so the sound too. */
const started = new Set()

/* `accountId` and `myAvatar` are the signed-in account and the picture its wallet wears: your own
   seat shows the picture you chose in the shop, where the game payload carries the one you wore
   when it was last sent. */
const MatchIntro = ({ game, onDone, accountId = null, myAvatar = null }) => {
  const { t } = useTranslation()
  const [leaving, setLeaving] = useState(false)
  const done = useRef(false)

  /* "A game starting sounds" (owner, 2026-10-23) — for a seat only. A spectator has no side and
     is being shown somebody else's war; the same reason the verdict says nothing to them. */
  const seated = accountId != null && (game.players ?? []).some((seat) => seat.user?.id === accountId)
  useEffect(() => {
    if (!seated || started.has(game.id)) return
    started.add(game.id)
    playUiSound("start")
  }, [game.id, seated])

  const finish = () => {
    if (done.current) return
    done.current = true
    rememberIntro(game.id)
    setLeaving(true)
    // The fade out, then the map.
    setTimeout(onDone, 450)
  }

  useEffect(() => {
    const timer = setTimeout(finish, INTRO_MS)
    const onKey = () => finish()
    window.addEventListener("keydown", onKey)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("keydown", onKey)
    }
    // Once, for the game it opened on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const teams = sides(game.players ?? [])
  const survival = isSurvival(game.mode)

  // Drawn on `body`: inside the game view it shared the map's stacking order, and the docks and
  // the top bar showed through it.
  return createPortal(
    <div
      className={`match-intro${leaving ? " leaving" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={t("game.intro.label")}
      onClick={finish}
      style={{ "--intro-ms": `${INTRO_MS}ms` }}
    >
      <div className="match-intro-head">
        <span className="match-intro-mode">{gameModeName(game.mode)}</span>
        <span className="match-intro-map">{mapLabel(game.mapSize)}</span>
      </div>

      <div className={`match-intro-board${teams.length > 1 ? "" : " single"}`}>
        {teams.map(({ team, seats }, index) => [
          index === 1 && (
            <span key="versus" className="intro-versus" aria-hidden="true">
              {t("game.intro.versus")}
            </span>
          ),
          <section key={team} className={`intro-side intro-side-${team} intro-side-${index === 0 ? "left" : "right"}`}>
            <h2 className="intro-side-title">{t(`game.intro.side_${team}`)}</h2>
            {survival && seats.every((seat) => seat.user?.isBot) ? (
              <p className="intro-horde">{t("game.intro.horde", { count: seats.length })}</p>
            ) : (
              <ul className="intro-seats">
                {seats.slice(0, MAX_SEATS_SHOWN).map((seat) => (
                  <Seat
                    key={seat.user?.id}
                    seat={seat}
                    t={t}
                    ownAvatar={accountId != null && seat.user?.id === accountId ? myAvatar : null}
                  />
                ))}
              </ul>
            )}
          </section>,
        ])}
      </div>

      <div className="match-intro-foot">
        <span className="match-intro-bar" aria-hidden="true" />
        <span className="match-intro-skip">{t("game.intro.skip")}</span>
      </div>
    </div>,
    document.body
  )
}

export default MatchIntro
